import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  Renderer2,
  signal,
  viewChild,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { resolveBootstrapColWidths } from '../../utils/bootstrap-col-widths';

/**
 * Unified input field with label + reactive forms.
 *
 * Supported types: `text`, `email`, `password`, `number`, `picker`, `code`.
 *
 * - `picker` — read-only display input with search/clear buttons (search icon: dots or magnifying glass via `generateCodeIcon`).
 * - `code`   — editable text input with a "generate" action button.
 */
@Component({
  selector: 'kl-input',
  standalone: false,
  templateUrl: './kl-input.html',
  styleUrl: './kl-input.scss',
})
export class KlInputComponent implements OnInit, AfterViewInit {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  protected readonly translate = inject(TranslateService);

  readonly formGroup = input.required<FormGroup>();
  readonly controlName = input.required<string>();

  readonly label = input('');
  readonly labelDescription = input('');
  readonly type = input<'text' | 'email' | 'password' | 'number' | 'picker' | 'code'>('text');
  readonly placeholder = input('');
  readonly autocomplete = input('');
  readonly autoFocus = input(false);

  readonly minlength = input<number | null>(null);
  readonly maxlength = input<number | null>(null);
  readonly pattern = input<string | RegExp | null>(null);
  readonly min = input<number | null>(null);
  readonly max = input<number | null>(null);

  readonly minFractionDigits = input(0);
  readonly maxFractionDigits = input(0);
  readonly useGrouping = input(true);

  readonly passwordToggleMask = input(true);
  readonly passwordFeedback = input(false);

  readonly inputId = input('');

  /** Bootstrap column classes on host (sm–xl inherit from smaller breakpoints when omitted). */
  readonly col = input('12');
  readonly sm = input<string | undefined>(undefined);
  readonly md = input<string | undefined>(undefined);
  readonly lg = input<string | undefined>(undefined);
  readonly xl = input<string | undefined>(undefined);
  readonly marginY = input(false);
  readonly marginBottom = input(true);

  /** When true, show validation / picker error text under the control. */
  readonly showFieldError = input(false);

  readonly patternMessage = input('');

  // ─── Picker-specific inputs ───
  /** Shown when {@link pickerDisplayFromControl} is false. */
  readonly pickerDisplayValue = input<string | undefined>(undefined);
  /**
   * When true, the readonly display string is read from `formGroup.getRawValue()[controlName]`
   * each change detection (so disabled controls still show correctly). Ignores `pickerDisplayValue`.
   */
  readonly pickerDisplayFromControl = input(false);
  /**
   * When true, the clear button sets the bound control to `''` and marks it touched before emitting
   * {@link pickerClear}. Use for simple string code fields; leave false for object-valued pickers.
   */
  readonly pickerClearToEmptyString = input(false);
  readonly pickerReadOnly = input(false);
  /** When true, the picker search button uses a magnifying glass (e.g. generate code); otherwise `ph-dots-three` (lookup). */
  readonly generateCodeIcon = input(false);
  readonly pickerErrorKey = input('important_area');

  // ─── Code-field-specific inputs ───
  readonly codeReadOnly = input(false);
  readonly codeGenerating = input(false);
  /** For `type="code"`: Phosphor class for the trailing action button (default the key/generate icon). */
  readonly codeButtonIcon = input('');
  /** For `type="code"`: show a clear (×) button while the control holds a value. */
  readonly codeShowClear = input(false);

  /** Optional trailing action on `type="number"` (i18n key, e.g. `detail`). */
  readonly addonButtonLabel = input('');
  /** Whether the addon detail panel is currently expanded (shows caret direction). */
  readonly addonButtonExpanded = input(false);

  readonly valueChange = output<unknown>();
  readonly pickerSearch = output<void>();
  readonly pickerClear = output<void>();
  readonly codeGenerate = output<void>();
  readonly addonButtonClick = output<void>();

  readonly plainInput = viewChild<ElementRef<HTMLInputElement>>('plainInput');

  readonly control = computed<AbstractControl | null>(() => this.formGroup()?.get(this.controlName()) ?? null);

  /**
   * `control.invalid`/`.touched`/`.errors` are plain mutable state, not signals, so a `computed()`
   * reading them never re-runs on its own — e.g. `form.markAllAsTouched()` from a save handler
   * wouldn't turn on this field's red border. Bump this on every control event so the
   * validation-derived computeds below stay live.
   */
  private readonly controlState = signal(0);
  private readonly controlStateSync = effect((onCleanup) => {
    const sub = this.control()?.events.subscribe(() => this.controlState.update((v) => v + 1));
    onCleanup(() => sub?.unsubscribe());
  });

  readonly fieldId = computed(() => (this.inputId() || this.controlName() || 'kl-input').replace(/\s/g, '_'));

  readonly required = computed(() => {
    this.controlState();
    const c = this.control();
    if (!c?.validator) return false;
    const v = c.validator({} as AbstractControl);
    return !!v?.['required'];
  });

  readonly showInvalid = computed(() => {
    this.controlState();
    const c = this.control();
    return !!(c && c.invalid && c.touched);
  });

  /** Readonly picker label; uses raw form value when {@link pickerDisplayFromControl} is true. */
  pickerDisplayText(): string {
    if (this.type() !== 'picker') return '';
    if (this.pickerDisplayFromControl()) {
      return this.formatValueForPickerDisplay(this.rawValueFromGroup());
    }
    return this.pickerDisplayValue() ?? '';
  }

  pickerHasDisplayValue(): boolean {
    const v = this.pickerDisplayFromControl() ? this.rawValueFromGroup() : this.control()?.value;
    if (v === null || v === undefined) return false;
    if (typeof v === 'string' && v.trim() === '') return false;
    if (typeof v === 'object') return Object.keys(v as object).length > 0;
    return true;
  }

  pickerShowError(): boolean {
    const c = this.control();
    return !!(c && c.touched && !this.pickerHasDisplayValue() && this.required());
  }

  onPickerClearClick(): void {
    if (this.pickerClearToEmptyString()) {
      const c = this.control();
      c?.setValue('');
      c?.markAsTouched();
    }
    this.pickerClear.emit();
  }

  onCodeClearClick(): void {
    const c = this.control();
    c?.setValue('');
    c?.markAsDirty();
    this.pickerClear.emit();
  }

  private rawValueFromGroup(): unknown {
    const g = this.formGroup();
    const n = this.controlName();
    if (!g || !n) return undefined;
    return (g.getRawValue() as Record<string, unknown>)[n];
  }

  private formatValueForPickerDisplay(v: unknown): string {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'object' && v !== null && 'code' in (v as object)) {
      const c = (v as { code?: unknown }).code;
      return c != null ? String(c) : '';
    }
    return String(v);
  }

  /**
   * Resolved validation message: either an i18n key (+ optional ngx-translate params)
   * or a raw string (custom pattern message from {@link patternMessage}).
   */
  readonly controlErrorI18n = computed(() => {
    this.controlState();
    const c = this.control();
    if (!c?.invalid || !c.touched) return null;
    if (this.pattern() && this.patternMessage() && c.errors?.['pattern']) {
      return { raw: this.patternMessage() } as const;
    }
    if (c.errors?.['required']) return { key: 'kl_input_validation_required' } as const;
    if (c.errors?.['email']) return { key: 'kl_input_validation_email' } as const;
    if (c.errors?.['minlength']) {
      const len = (c.errors['minlength'] as { requiredLength: number }).requiredLength;
      return { key: 'kl_input_validation_minlength', params: { requiredLength: len } } as const;
    }
    if (c.errors?.['maxlength']) {
      const len = (c.errors['maxlength'] as { requiredLength: number }).requiredLength;
      return { key: 'kl_input_validation_maxlength', params: { requiredLength: len } } as const;
    }
    if (c.errors?.['min']) {
      const min = (c.errors['min'] as { min: number }).min;
      return { key: 'kl_input_validation_min', params: { min } } as const;
    }
    if (c.errors?.['max']) {
      const max = (c.errors['max'] as { max: number }).max;
      return { key: 'kl_input_validation_max', params: { max } } as const;
    }
    return null;
  });

  ngOnInit(): void {
    const el = this.host.nativeElement;
    const col = this.col();
    const { sm, md, lg, xl } = resolveBootstrapColWidths(col, this.sm(), this.md(), this.lg(), this.xl());
    this.renderer.addClass(el, 'col-' + col);
    this.renderer.addClass(el, 'col-sm-' + sm);
    this.renderer.addClass(el, 'col-md-' + md);
    this.renderer.addClass(el, 'col-lg-' + lg);
    this.renderer.addClass(el, 'col-xl-' + xl);
  }

  ngAfterViewInit(): void {
    if (!this.autoFocus()) return;
    const t = this.type();
    if (t === 'text' || t === 'email') {
      queueMicrotask(() => this.plainInput()?.nativeElement?.focus());
    } else {
      queueMicrotask(() => document.getElementById(this.fieldId())?.focus());
    }
  }

  onAnyChange(): void {
    this.valueChange.emit(this.control()?.value);
  }
}
